import { useEffect, useState } from 'react';
import { isPortaledEvent } from './event.js';
import { usePersistentPopupOutletOrNull } from './persistent-popup-outlet.js';

/**
 * For popups that are persistent and should be stacked alongside windows, call
 * usePopupOrdering to set up stacking on your props
 *
 * @param props - pass in ALL props you need for your component
 * @param options:
 *    @param popupClosed - if the popup can be rendered in a closed state, pass a boolean
 *  .                      here so that the popup will be brought to front when opened
 *
 * @returns props, these should be spread in LAST to your component
 */
function usePopupOrdering(props, options) {
    const outlet = usePersistentPopupOutletOrNull();
    const { popupClosed } = options || {};
    const [zIndex, bringToFront] = useBringToFront();
    const onPopupFocused = (e) => {
        if (!isPortaledEvent(e)) {
            bringToFront();
        }
    };
    useEffect(() => {
        // Specifically check for false, we want to ignore 'undefined' here
        if (popupClosed === false && outlet) {
            bringToFront();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [popupClosed]);
    const onFocusCapture = (e) => {
        onPopupFocused(e);
        props.onFocusCapture?.(e);
    };
    const onPointerDownCapture = (e) => {
        onPopupFocused(e);
        props.onPointerDownCapture?.(e);
    };
    if (!outlet || popupClosed === true) {
        return props;
    }
    return {
        ...props,
        style: { ...(props.style ?? {}), zIndex },
        onFocusCapture,
        onPointerDownCapture,
    };
}
let nextZ = 0;
/**
 * Get the next z-index value and a function to grab the next highest z-index.
 * We can safely use an ever-increasing z-index here because all popups
 * renders inside of the PersistentPopupOutlet, which portals to a container that sets `isolation: isolate`
 * so that the z-index is relative only to to the other popups.
 */
function useBringToFront() {
    const [zIndex, setZIndex] = useState(() => nextZ++);
    const bringToFront = () => {
        setZIndex((currZ) => {
            // If the current z-index is already the highest, don't increment it
            if (currZ === nextZ - 1)
                return currZ;
            return nextZ++;
        });
    };
    return [zIndex, bringToFront];
}

export { usePopupOrdering };
//# sourceMappingURL=persistent-popup-hooks.js.map
